import React from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';

/**
 * SidebarContextBlock — the "where am I" button under the sidebar logo
 * (ticket #295). Shows the selected record and opens its select panel.
 * Bids pass the name as headline and the bid number as subline; #290 will
 * pass the project number first. Includes its own sidebar padding.
 *
 * Props:
 *   headline    {node}   main line, 15px / 600, clamped to 2 lines
 *   subline     {node}   second line, 12px gray, tabular numerals
 *   onClick     {func}   opens the select panel
 *   open        {bool}   the panel is open (lighter fill + primary border)
 *   emptyLabel  {string} shown when there is no headline ("Select a bid")
 *   tooltip     {node}   full name + number; hover / focus
 *   collapsed   {bool}   narrow sidebar: a chip with chipLabel
 *   chipLabel   {node}   collapsed chip text (the number)
 *   emptyIcon   {element} collapsed icon when nothing is selected
 */
const SidebarContextBlock = ({
  headline,
  subline,
  onClick,
  open = false,
  emptyLabel = 'Select',
  tooltip,
  collapsed = false,
  chipLabel,
  emptyIcon = <ExpandMoreOutlined />,
}) => {
  const { ft } = useTheme().palette;
  const isEmpty = !headline;
  const popupProps = { 'aria-haspopup': 'dialog', 'aria-expanded': open };

  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 1, pb: 1.5 }}>
        {isEmpty || !chipLabel ? (
          <Tooltip title={emptyLabel} placement="right">
            <IconButton
              onClick={onClick}
              aria-label={emptyLabel}
              {...popupProps}
              sx={{
                color: ft.sidebar.text,
                '&:hover': {
                  backgroundColor: ft.sidebar.activeBg,
                  color: ft.sidebar.textActive,
                },
              }}
            >
              {emptyIcon}
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={tooltip || ''} placement="right" arrow>
            <ButtonBase
              onClick={onClick}
              {...popupProps}
              sx={{
                height: 26,
                maxWidth: '100%',
                px: 1,
                borderRadius: '13px',
                backgroundColor: ft.sidebar.activeBg,
                color: ft.sidebar.textActive,
                fontFamily: ft.fontFamily,
                fontSize: 11,
                fontWeight: ft.fontWeights.semibold,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                '&:hover': { backgroundColor: ft.sidebar.hoverBg },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${ft.primary.main}`,
                  outlineOffset: 1,
                },
              }}
            >
              {chipLabel}
            </ButtonBase>
          </Tooltip>
        )}
      </Box>
    );
  }

  const block = (
    <ButtonBase
      onClick={onClick}
      {...popupProps}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: '12px 8px',
        borderRadius: '6px',
        textAlign: 'center',
        fontFamily: ft.fontFamily,
        color: ft.sidebar.text,
        backgroundColor: open ? ft.sidebar.blockOpenBg : ft.sidebar.blockBg,
        border: `1px solid ${open ? ft.primary.main : ft.sidebar.blockBorder}`,
        '&:hover': { backgroundColor: ft.sidebar.blockOpenBg },
        '&.Mui-focusVisible': {
          outline: `2px solid ${ft.primary.main}`,
          outlineOffset: 1,
        },
      }}
    >
      {/* Matches the chevron so the text stays centered. */}
      <Box aria-hidden sx={{ width: 20, flex: 'none' }} />
      {isEmpty ? (
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            py: 0.75,
            fontSize: 14,
            fontWeight: ft.fontWeights.medium,
            color: ft.sidebar.textLight,
          }}
        >
          {emptyLabel}
        </Box>
      ) : (
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '3px',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 15,
              fontWeight: ft.fontWeights.semibold,
              lineHeight: 1.3,
              color: ft.sidebar.textActive,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {headline}
          </Typography>
          {subline && (
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                lineHeight: 1.3,
                color: ft.sidebar.text,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {subline}
            </Typography>
          )}
        </Box>
      )}
      <ExpandMoreOutlined sx={{ fontSize: 20, flex: 'none' }} />
    </ButtonBase>
  );

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      {tooltip && !isEmpty ? (
        <Tooltip title={tooltip} placement="right" arrow enterDelay={400}>
          {block}
        </Tooltip>
      ) : (
        block
      )}
    </Box>
  );
};

export default SidebarContextBlock;
